import { FormikProps } from 'formik';
import React, { ChangeEvent, VFC } from 'react';
import { Checkbox } from 'src/common/form/checkbox';
import { FormikTextInput } from 'src/common/form/formik/text-input';
import { Creator } from 'src/common/interfaces/creator.interface';
import { OrganizationUser } from 'src/common/interfaces/organization/user.interface';
import { Workspace } from 'src/common/interfaces/workspace.interface';
import { JourneyForm, JourneyFormik } from 'src/common/JourneyFormik';
import { Nullable } from 'src/types/nullable.type';
import * as Yup from 'yup';

export interface EditableWorkspaceForm {
  id: Nullable<number>;
  name: string;
  user_ids: number[];
  editingWorkspaceId: Nullable<number>;
}

interface DashboardSettingsManageWorkspacesFormProps {
  members: Creator[];
  workspace: Nullable<Workspace>,
  canEditMembers: boolean;
  currentUser: Nullable<OrganizationUser>;
  onSubmit: (values: Record<string, string>) => void;
}

export const DashboardSettingsManageWorkspacesForm: VFC<DashboardSettingsManageWorkspacesFormProps> = ({
  members,
  workspace,
  canEditMembers = true,
  currentUser,
  onSubmit,
}) => {
  const initialMembers = currentUser ? [currentUser.id] : [];

  const formikParams = {
    initialValues: {
      name: workspace ? workspace.name : '',
      user_ids: workspace ? workspace.members.map((member: any) => member.id) : initialMembers,
    },
    validationSchema: Yup.object({ name: Yup.string().required(), user_ids: Yup.array().of(Yup.number()).min(1) }),
    onSubmit,
  };

  return (
    <JourneyFormik {...formikParams}>
      {({ values, setFieldValue, errors }: FormikProps<any>) => {
        const onMemberCheckboxChanged = (memberId: number) => (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
          let { user_ids } = values;
          if (checked) {
            user_ids.push(memberId);
          } else {
            user_ids = user_ids.filter((id: number) => id !== memberId);
          }
          setFieldValue('user_ids', [...user_ids]);
        };

        return (
          <JourneyForm>
            <div className='flex flex-col space-y-6'>
              <div className='flex flex-col space-y-sm'>
                <FormikTextInput
                  label='Workspace name'
                  id='workspace-name'
                  name='name'
                  required
                  type='text'
                  placeholder='Name your workspace'
                />
                <span className='text-bedrock-p-strong text-bedrock-black'>Add members</span>
                {members.map((member) => (
                  <Checkbox
                    key={`workspace-checkbox-${member.id}`}
                    disabled={!canEditMembers}
                    checked={values.user_ids.includes(member.id)}
                    onChange={onMemberCheckboxChanged(member.id)}
                    isBedrock
                    label={member.name}
                  />
                ))}
              </div>
              <button
                type='submit'
                className='btn-bedrock-primary'
                disabled={(values.name === '' && values.user_ids.length === 0) || Object.keys(errors).length > 0}
              >
                {workspace ? 'Update workspace' : 'Create workspace'}
              </button>
            </div>
          </JourneyForm>
        );
      }}
    </JourneyFormik>
  );
};
